// ClaudeCode: Lock screen — prompts for vault passphrase to derive encryption key
// First-time setup: requires confirmation + explains severity
// Returning user: single passphrase entry
import React, { useState } from 'react';

interface LockScreenProps {
  displayName: string;
  isFirstTime: boolean; // true = new account, no passphrase set yet
  onUnlock: (passphrase: string) => Promise<void>;
  onLogout: () => void;
}

export function LockScreen({ displayName, isFirstTime, onUnlock, onLogout }: LockScreenProps) {
  const [passphrase, setPassphrase] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passphrase) return;

    if (isFirstTime) {
      // Validate confirmation
      if (passphrase !== confirm) {
        setError('Passphrases do not match.');
        return;
      }
      if (passphrase.length < 8) {
        setError('Passphrase must be at least 8 characters.');
        return;
      }
      if (!acknowledged) {
        setError('You must acknowledge that this passphrase cannot be recovered.');
        return;
      }
    }

    setLoading(true);
    setError('');
    try {
      await onUnlock(passphrase);
    } catch {
      setError('Failed to derive key. Check your passphrase.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 24,
      }}
    >
      <div
        style={{
          background: '#18181b',
          border: '1px solid #27272a',
          borderRadius: 12,
          padding: 32,
          width: '100%',
          maxWidth: 460,
        }}
      >
        {/* Lock icon */}
        <div style={{ textAlign: 'center', fontSize: 48, marginBottom: 8 }}>
          {isFirstTime ? '\uD83D\uDD10' : '\uD83D\uDD12'}
        </div>

        <h2
          style={{
            textAlign: 'center',
            fontSize: 20,
            fontWeight: 600,
            color: '#fafafa',
            marginBottom: 4,
          }}
        >
          {isFirstTime ? 'Create Your Vault Passphrase' : 'Vault Locked'}
        </h2>
        <p
          style={{
            textAlign: 'center',
            fontSize: 14,
            color: '#71717a',
            marginBottom: isFirstTime ? 16 : 24,
          }}
        >
          {isFirstTime
            ? `Welcome, ${displayName}. Let\u2019s secure your vault.`
            : `Welcome back, ${displayName}`}
        </p>

        {/* First-time warning */}
        {isFirstTime && (
          <div
            style={{
              background: '#451a03',
              border: '1px solid #92400e',
              borderRadius: 8,
              padding: 16,
              marginBottom: 20,
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            <div style={{ fontWeight: 700, color: '#fbbf24', marginBottom: 8 }}>
              Important — Read Before Continuing
            </div>
            <div style={{ color: '#fde68a' }}>
              Your vault passphrase is the <strong>only key</strong> that can
              decrypt your secrets. It is never sent to our servers and we
              cannot recover it for you.
            </div>
            <ul
              style={{
                color: '#fde68a',
                margin: '10px 0 0 0',
                paddingLeft: 18,
              }}
            >
              <li style={{ marginBottom: 4 }}>
                This is <strong>not</strong> the same as your login password
              </li>
              <li style={{ marginBottom: 4 }}>
                If you lose this passphrase, your encrypted data is{' '}
                <strong>permanently gone</strong>
              </li>
              <li style={{ marginBottom: 4 }}>
                No one — not even us — can reset or recover it
              </li>
              <li>
                Choose something strong and memorable, then store a backup
                somewhere safe
              </li>
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <label
            style={{
              fontSize: 13,
              color: '#a1a1aa',
              display: 'block',
              marginBottom: 6,
            }}
          >
            {isFirstTime ? 'Choose a Vault Passphrase' : 'Vault Passphrase'}
          </label>
          <input
            type="password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            autoFocus
            placeholder={
              isFirstTime
                ? 'Minimum 8 characters — make it strong'
                : 'Enter your vault passphrase'
            }
            style={inputStyle}
          />

          {/* Confirmation field — first time only */}
          {isFirstTime && (
            <>
              <label
                style={{
                  fontSize: 13,
                  color: '#a1a1aa',
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                Confirm Passphrase
              </label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Type it again to confirm"
                style={inputStyle}
              />

              {/* Acknowledgment checkbox */}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  fontSize: 13,
                  color: '#a1a1aa',
                  cursor: 'pointer',
                  marginBottom: 16,
                  lineHeight: 1.4,
                }}
              >
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <span>
                  I understand that if I lose this passphrase and have no
                  recovery key, my encrypted data will be{' '}
                  <strong style={{ color: '#f87171' }}>
                    permanently unrecoverable
                  </strong>
                  .
                </span>
              </label>
            </>
          )}

          {error && (
            <div
              style={{
                color: '#f87171',
                fontSize: 13,
                marginBottom: 12,
                textAlign: 'center',
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !passphrase || (isFirstTime && (!confirm || !acknowledged))}
            style={{
              width: '100%',
              padding: '10px 0',
              background:
                loading || (isFirstTime && (!confirm || !acknowledged))
                  ? '#27272a'
                  : '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 15,
              fontWeight: 600,
              cursor: loading ? 'wait' : 'pointer',
              marginBottom: 12,
            }}
          >
            {loading
              ? 'Deriving encryption key...'
              : isFirstTime
              ? 'Create Vault'
              : 'Unlock'}
          </button>

          <button
            type="button"
            onClick={onLogout}
            style={{
              width: '100%',
              padding: '8px 0',
              background: 'none',
              color: '#71717a',
              border: 'none',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Switch account
          </button>
        </form>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  background: '#09090b',
  border: '1px solid #27272a',
  borderRadius: 6,
  color: '#fafafa',
  fontSize: 15,
  outline: 'none',
  marginBottom: 16,
};
